import { POWERUP_LIST } from "@/shared/data/powerups";
import { POWERUP_ICONS } from "@/shared/icons/powerups";
import { useGameStore } from "@/shared/state/gameStore";
import { UnlockRequirement } from "@/shared/types/unlock";
import {
  canPurchasePowerup,
  getPowerupCost,
  isPowerupUnlocked,
} from "@/shared/utils/powerups";
import { useEffect, useState } from "react";
import { useGameRuntime } from "../context/GameContext";

interface PowerupShopSheetProps {
  open: boolean;
  onClose: () => void;
}

const formatRequirement = (requirement: UnlockRequirement) => {
  switch (requirement.type) {
    case "likes":
      return `${Math.floor(requirement.amount)} likes`;
    case "powerup_level":
      return `${requirement.powerupId.replace(/_/g, " ")} lv.${requirement.level}`;
    case "animation_unlocked":
      return `${requirement.animationId.replace(/_/g, " ")} unlocked`;
    case "chat_unlocked":
      return "Chat unlocked";
  }
};

const PowerupShopSheet = ({ open, onClose }: PowerupShopSheetProps) => {
  const runtime = useGameRuntime();
  const state = useGameStore();
  const visiblePowerups = POWERUP_LIST.filter((powerup) => {
    const unlocked = isPowerupUnlocked(state, powerup);
    const level = state.powerups[powerup.id] ?? 0;
    const maxed = powerup.maxLevel != null && level >= powerup.maxLevel;
    return unlocked && !maxed;
  }).sort((left, right) => left.scaling.baseCost - right.scaling.baseCost);

  const [selectedId, setSelectedId] = useState(visiblePowerups[0]?.id);

  useEffect(() => {
    if (
      !selectedId ||
      visiblePowerups.some((powerup) => powerup.id === selectedId)
    ) {
      return;
    }

    setSelectedId(visiblePowerups[0]?.id);
  }, [selectedId, visiblePowerups]);

  const selected =
    visiblePowerups.find((powerup) => powerup.id === selectedId) ??
    visiblePowerups[0];

  if (!selected) return null;

  const selectedLevel = state.powerups[selected.id] ?? 0;
  const selectedCost = getPowerupCost(selected, selectedLevel);
  const selectedUnlocked = isPowerupUnlocked(state, selected);
  const selectedPurchasable = canPurchasePowerup(state, selected);
  const selectedMaxed =
    selected.maxLevel != null && selectedLevel >= selected.maxLevel;

  return (
    <div
      className={[
        "absolute inset-0 z-[160] select-none transition",
        open ? "pointer-events-auto" : "pointer-events-none",
      ].join(" ")}
      onClick={onClose}
    >
      <div
        className={[
          "absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(168,85,247,0.14),_transparent_35%),rgba(6,8,18,0.48)] transition duration-300",
          open ? "opacity-100" : "opacity-0",
        ].join(" ")}
      />
      <div
        onClick={(event) => event.stopPropagation()}
        className={[
          "absolute inset-x-3 bottom-3 mx-auto w-auto max-w-md overflow-hidden rounded-[2rem] border border-fuchsia-200/10 bg-[linear-gradient(180deg,rgba(7,8,16,0.94),rgba(7,8,16,0.98))] text-white shadow-[0_-24px_80px_rgba(12,10,30,0.6)] backdrop-blur-xl transition-all duration-300 sm:inset-x-4 sm:bottom-4",
          open ? "translate-y-0 opacity-100" : "translate-y-[110%] opacity-0",
        ].join(" ")}
      >
        <div className="w-full px-5 pt-4 pb-4 text-left">
          <div>
            <p className="text-[10px] uppercase tracking-[0.35em] text-fuchsia-200/75">
              Shop
            </p>
            <p className="text-sm font-semibold text-white">Stream Upgrades</p>
          </div>
        </div>

        <div className="border-t border-white/8 px-4 pt-4 pb-5">
          <div className="grid max-h-64 grid-cols-3 gap-3 overflow-y-auto pr-1 sm:grid-cols-4">
            {visiblePowerups.map((powerup) => {
              const Icon = POWERUP_ICONS[powerup.icon];
              const level = state.powerups[powerup.id] ?? 0;
              const affordable = canPurchasePowerup(state, powerup);
              const cost = getPowerupCost(powerup, level);
              const active = powerup.id === selected.id;

              return (
                <button
                  key={powerup.id}
                  type="button"
                  onClick={() => setSelectedId(powerup.id)}
                  className={[
                    "group relative aspect-square rounded-[1.4rem] border p-2.5 text-left transition sm:p-3",
                    active
                      ? "border-fuchsia-300/36 bg-fuchsia-400/10 shadow-[0_0_0_1px_rgba(217,70,239,0.18)]"
                      : "border-white/8 bg-white/4 hover:border-fuchsia-200/20 hover:bg-white/[0.07]",
                  ].join(" ")}
                >
                  <div
                    className={[
                      "pointer-events-none absolute top-2 right-2 rounded-full border border-white/10 bg-black/35 px-2 py-0.5 text-[10px] font-medium text-white/75 transition",
                      active
                        ? "opacity-100"
                        : "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100",
                    ].join(" ")}
                  >
                    {`Lv ${level}`}
                  </div>

                  <div className="flex h-full items-center justify-center">
                    <div
                      className={[
                        "flex h-14 w-14 items-center justify-center rounded-[1.2rem] border transition sm:h-16 sm:w-16",
                        active
                          ? "border-fuchsia-200/22 bg-black/34"
                          : "border-white/8 bg-black/28 group-hover:border-fuchsia-200/16",
                      ].join(" ")}
                    >
                      <Icon
                        className={[
                          "h-6 w-6",
                          affordable ? "text-fuchsia-50" : "text-white/72",
                        ].join(" ")}
                      />
                    </div>
                  </div>

                  <div className="pointer-events-none absolute inset-x-2 bottom-3">
                    <p
                      className={[
                        "line-clamp-2 text-center text-[11px] font-semibold leading-4 text-white transition duration-150 sm:text-xs",
                        active
                          ? "opacity-0 blur-[2px]"
                          : "group-hover:opacity-0 group-hover:blur-[2px] group-focus-visible:opacity-0 group-focus-visible:blur-[2px]",
                      ].join(" ")}
                    >
                      {powerup.name}
                    </p>
                    <div
                      className={[
                        "absolute inset-0 flex items-center justify-center transition duration-150",
                        active
                          ? "opacity-100"
                          : "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100",
                      ].join(" ")}
                    >
                      <span
                        className={[
                          "rounded-full border px-2.5 py-1 text-[10px] font-medium backdrop-blur-sm",
                          affordable
                            ? "border-fuchsia-300/24 bg-fuchsia-400/10 text-fuchsia-100"
                            : "border-white/10 bg-black/50 text-white/70",
                        ].join(" ")}
                      >
                        {`${Math.floor(cost)} likes`}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-4 rounded-[1.6rem] border border-white/8 bg-white/[0.04] p-4 backdrop-blur-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-200/75">
                  Selected
                </p>
                <h3 className="mt-1 text-lg font-semibold text-white">
                  {selected.name}
                </h3>
              </div>
              <div className="rounded-full border border-white/10 bg-black/30 px-3 py-1 text-xs font-medium text-white/70">
                Level {selectedLevel}
              </div>
            </div>

            <p className="mt-3 text-sm leading-relaxed text-white/78">
              {selected.description}
            </p>

            {selected.unlockRequirements && !selectedUnlocked ? (
              <div className="mt-4 rounded-2xl border border-fuchsia-300/12 bg-fuchsia-400/6 px-3 py-2">
                <p className="text-[10px] uppercase tracking-[0.25em] text-fuchsia-100/75">
                  Unlock Requirements
                </p>
                <p className="mt-2 text-xs leading-relaxed text-white/82">
                  {selected.unlockRequirements
                    .map(formatRequirement)
                    .join(" | ")}
                </p>
              </div>
            ) : null}

            <div className="mt-4 flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">
                  Next Cost
                </p>
                <p className="mt-1 text-xl font-semibold text-white">
                  {selectedMaxed ? "MAX" : `${Math.floor(selectedCost)} likes`}
                </p>
              </div>

              <button
                type="button"
                onClick={() => runtime.buyPowerup(selected.id)}
                disabled={!selectedPurchasable}
                className={[
                  "rounded-full px-5 py-3 text-sm font-semibold transition",
                  selectedPurchasable
                    ? "bg-fuchsia-300/90 text-slate-950 hover:bg-fuchsia-200"
                    : "cursor-not-allowed bg-white/10 text-white/35",
                ].join(" ")}
              >
                {selectedMaxed
                  ? "Maxed"
                  : !selectedUnlocked
                    ? "Locked"
                    : selected.id === "retirement"
                      ? "Finish Run"
                      : "Purchase"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PowerupShopSheet;
